import { ErrorTypes } from '@fabric-ui/components';
import { AuthStorageKeys } from '@utils/localStorageAuthUtils.utils';
import { JWT } from 'aws-amplify/auth';
import { gql } from 'graphql-request';
import { AuthenticationProvider, AuthPlatform, SessionInfo, UserInfo } from './auth.types';
import { handleCodeFlow } from './zid-oidc/completeOAuthFlow';
import { fetchAuthSession } from './zid-oidc/fetchAuthSession';
import { getCurrentUser } from './zid-oidc/getCurrentUser';
import { overrideAccountAttribute } from './zid-oidc/overrideAccountAttribute';
import { signInWithRedirect } from './zid-oidc/signInWithRedirect';
import { ZidAuthLocalStorageKeys, ZidAuthSessionStorageKeys, ZidAuthUrlParams } from './zid-oidc/tokenUtils';

export const ZID_ERROR_PREFIX = '[ZIdentity Error]';

const configureAuthProvider = async (router): Promise<void> => {
  const params = new URLSearchParams(window.location.search);
  const authorizationCode = params.get(ZidAuthUrlParams.code);
  const state = params.get(ZidAuthUrlParams.state);

  if (!authorizationCode && sessionStorage.getItem(ZidAuthSessionStorageKeys.oauthState)) {
    console.error(`${ErrorTypes.Authentication} ${ZID_ERROR_PREFIX} User canceled authentication flow`);
    sessionStorage.clear();
  }

  if (authorizationCode && state) {
    await processAuthRedirect(router);
    return;
  }

  const authConfig = localStorage.getItem(AuthStorageKeys.authConfig);
  const accessToken = localStorage.getItem(ZidAuthLocalStorageKeys.accessToken);

  if (authConfig && !accessToken && state) {
    console.error(
      `${ErrorTypes.Authentication} ${ZID_ERROR_PREFIX} Inconsistent state detected: Auth config exists but no access token. Clearing storage to force re-authentication.`
    );
    clearLS();
  }
};

const formatLocalSessionObject = ({ session }): UserInfo => ({
  email: session.userMetadata.userEmail as unknown as string,
  userId: session.userMetadata?.userId as unknown as string,
  roleId: session.roleMetadata?.roleId as unknown as string,
  accountId: session.accountMetadata?.accountId as unknown as string,
  abac: session.userMetadata?.abacFilters as unknown as string,
  permissions: session.roleMetadata?.rolePermissions as unknown as string,
  userName: session.userMetadata?.rolePermissions as unknown as string,
  roleName: session.roleMetadata?.roleName as unknown as string,
});

const fetchSession = async (): Promise<SessionInfo> => {
  try {
    const res = await fetchAuthSession();
    const accountToken = res.tokens?.accessToken as JWT;
    const defaultAccountId: string | undefined = getFabricServiceInfo(accountToken)?.tnt;
    return { accountToken, userInfo: res.session, defaultAccountId };
  } catch (error) {
    console.error(
      `${ErrorTypes.Authentication} ${ZID_ERROR_PREFIX}: Fetch session error: ${(error as any).message} ${(error as any).underlyingError} `
    );
    return { error: (error as any).underlyingError };
  }
};

export const getFabricServiceInfo = accountToken => {
  const serviceInfo = accountToken?.payload['service-info'];
  return Array.isArray(serviceInfo) ? serviceInfo.find(info => info.prd === 'FABRIC') : undefined;
};

export const clearLS = () => {
  const zidLSValues = Object.keys(localStorage).filter(key => /^zid(.*)$/.test(key));
  zidLSValues.forEach(key => localStorage.removeItem(key));
  sessionStorage.clear();
};

const signOut = async () => {
  clearLS();
  window.location.replace(window.location.origin);
};

const overrideAccountId = async (accountId: string, attempts = 0): Promise<void> => {
  try {
    await overrideAccountAttribute(accountId);
  } catch (error) {
    if (attempts < 1) {
      await overrideAccountId(accountId, attempts + 1);
    } else {
      console.error(
        `${ErrorTypes.Authentication} ${ZID_ERROR_PREFIX}: Error changing account ID user attribute to: ${accountId} after 2 attempts ${JSON.stringify(
          (error as any).message || error
        )}`
      );
    }
  }
};

const signIn = async (state?: string): Promise<void> =>
  signInWithRedirect({ customState: encodeURIComponent(state || window.location.pathname + window.location.search) });

const getUser = async () => {
  try {
    await getCurrentUser();
  } catch (error) {
    throw new Error(`${ErrorTypes.Authentication} ${ZID_ERROR_PREFIX} ${error}`);
  }
};

const processAuthRedirect = async router => {
  try {
    await handleCodeFlow(router);
  } catch (error) {
    clearLS();
    localStorage.removeItem(AuthStorageKeys.authConfig);
    console.error(`${ErrorTypes.Authentication} ${ZID_ERROR_PREFIX} ${error}`);
    window.location.reload();
  }
};

const getAccountFromToken = (decodedToken: JWT): string => getFabricServiceInfo(decodedToken)?.ext?.sid;

const getAdditionalUserData = gql`
  query getCustomerFilteredUserAdditionalData {
    getCustomerFilteredUserAdditionalData {
      accounts {
        accountId
        accountName
        userRoleId
      }
      favoriteApps
    }
  }
`;

export const zidAuthenticationProvider: AuthenticationProvider = {
  configureAuthProvider,
  fetchSession,
  signOut,
  overrideAccountId,
  signIn,
  clearLS,
  isUserAuthenticated: getUser,
  processAuthRedirect,
  getAccountFromToken,
  formatLocalSessionObject,
  getAdditionalUserData,
  provider: AuthPlatform.ZID,
};
